/**
 * ServiceTierCard — Settings > Integrations > ALICE Plan.
 *
 * Shows the tenant's ALICE plan. When a super admin has moved the tenant to
 * a plan that uses a different ALICE app registration, the change waits for
 * this tenant's administrator to approve that registration's permissions;
 * the card sends them to Microsoft admin consent and then completes the
 * switch. The tenant keeps its current plan until then.
 *
 * Backend contract:
 *   GET  /api/tenant/settings/service-tier
 *   POST /api/tenant/settings/service-tier/complete
 *
 * Consent round trip: Microsoft only returns to a registered redirect URI,
 * so this reuses onboarding's (/onboarding). TenantOnboardingPage spots the
 * state minted here and sends the admin back to
 * /settings?section=integrations&plan_consent=granted|error.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  AlertTriangle,
  CheckCircle2,
  Layers,
  Loader2,
  RefreshCw,
  ShieldCheck,
} from "lucide-react";
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

export const SERVICE_TIER_CONSENT_PURPOSE = "change_service_tier";

// Entra can take a minute or two to put freshly consented permissions on
// the tokens it issues, so keep trying for about two minutes.
const VERIFY_INTERVAL_MS = 10000;
const VERIFY_ATTEMPTS = 12;

// Reads the state Microsoft echoes back from /adminconsent. Returns the
// decoded object only when it was minted by this card.
export function parseServiceTierConsentState(state) {
  if (!state) return null;
  try {
    const decoded = JSON.parse(atob(state));
    return decoded?.purpose === SERVICE_TIER_CONSENT_PURPOSE ? decoded : null;
  } catch {
    return null;
  }
}

function buildConsentUrl(tenantId, clientId, tier) {
  const state = btoa(JSON.stringify({
    purpose: SERVICE_TIER_CONSENT_PURPOSE,
    tenantId,
    serviceTier: tier,
    timestamp: Date.now(),
  }));
  // Tenant-specific authority so consent lands in this tenant even when the
  // admin is a guest elsewhere.
  const url = new URL(
    `https://login.microsoftonline.com/${encodeURIComponent(tenantId)}/adminconsent`
  );
  url.searchParams.set("client_id", clientId);
  url.searchParams.set("redirect_uri", `${window.location.origin}/onboarding`);
  url.searchParams.set("state", state);
  return url.toString();
}

function errorText(error, fallback) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  return detail?.message || error?.message || fallback;
}

export default function ServiceTierCard({
  apiBase,
  authHeaders,
  consentResult,
  consentError,
  onConsentHandled,
}) {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [completing, setCompleting] = useState(false);
  const [notice, setNotice] = useState(null); // { tone, text }
  const [applied, setApplied] = useState(null);

  const authHeadersRef = useRef(authHeaders);
  authHeadersRef.current = authHeaders;
  const mountedRef = useRef(true);
  const startedRef = useRef(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = await authHeadersRef.current();
      const r = await axios.get(`${apiBase}/tenant/settings/service-tier`, { headers });
      if (mountedRef.current) setInfo(r.data);
    } catch (e) {
      if (mountedRef.current) setError(errorText(e, "Could not load the ALICE plan"));
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [apiBase]);

  const tryComplete = useCallback(async () => {
    const headers = await authHeadersRef.current();
    const r = await axios.post(`${apiBase}/tenant/settings/service-tier/complete`, {}, { headers });
    return r.data;
  }, [apiBase]);

  // True when the attempt reached a final answer.
  const handleResult = useCallback((data) => {
    if (data?.status === "applied") {
      setApplied(data);
      setNotice(null);
      toast.success("ALICE plan updated");
      return true;
    }
    if (data?.status === "blocked" || data?.status === "no_pending_change") {
      setNotice({ tone: data.status === "blocked" ? "error" : "info", text: data.message });
      return true;
    }
    return false;
  }, []);

  const verifyAfterConsent = useCallback(async () => {
    setCompleting(true);
    setNotice({
      tone: "info",
      text: "Admin consent granted. Waiting for Microsoft Entra to apply the new permissions - this usually takes under a minute.",
    });
    for (let attempt = 1; attempt <= VERIFY_ATTEMPTS; attempt++) {
      try {
        const data = await tryComplete();
        if (!mountedRef.current) return;
        if (handleResult(data)) {
          setCompleting(false);
          refresh();
          return;
        }
        if (data?.message) setNotice({ tone: "info", text: data.message });
      } catch (e) {
        if (!mountedRef.current) return;
        setError(errorText(e, "Could not complete the plan change"));
      }
      if (attempt < VERIFY_ATTEMPTS) {
        await new Promise((resolve) => setTimeout(resolve, VERIFY_INTERVAL_MS));
        if (!mountedRef.current) return;
      }
    }
    setCompleting(false);
    setNotice({
      tone: "warning",
      text: "Consent was recorded but the new permissions haven't appeared yet. Entra can take a few minutes - use Check again shortly. The approving account must be a Global Administrator or Privileged Role Administrator.",
    });
  }, [tryComplete, handleResult, refresh]);

  const checkAgain = async () => {
    setCompleting(true);
    setError(null);
    try {
      const data = await tryComplete();
      if (!handleResult(data)) {
        setNotice({ tone: "warning", text: data?.message || "The new permissions aren't in place yet." });
      } else {
        refresh();
      }
    } catch (e) {
      setError(errorText(e, "Could not complete the plan change"));
    } finally {
      setCompleting(false);
    }
  };

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    refresh();
    if (!consentResult) return;
    // Clear the consent flags from the URL so a refresh doesn't replay them.
    onConsentHandled?.();
    if (consentResult === "granted") {
      verifyAfterConsent();
    } else {
      setNotice({
        tone: "error",
        text: consentError
          ? `Admin consent was not completed: ${consentError}`
          : "Admin consent was not completed.",
      });
    }
    // Runs once on mount: the consent result is only meaningful on arrival.
  }, []);

  if (info && info.applicable === false) return null;

  const pending = applied ? null : info?.pending;
  const approve = () => {
    if (!pending?.client_id || !info?.tenant_id) return;
    window.location.href = buildConsentUrl(info.tenant_id, pending.client_id, pending.tier);
  };

  return (
    <Card data-testid="service-tier-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 font-['Chivo']">
          <Layers className="h-5 w-5" />
          ALICE Plan
        </CardTitle>
        <CardDescription>
          Your plan decides what ALICE can do in your Microsoft Entra tenant and
          which ALICE application holds its permissions.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {loading && !info ? (
          <div className="flex items-center gap-2 text-sm text-white/60">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading plan…
          </div>
        ) : info ? (
          <p className="text-sm" data-testid="service-tier-current">
            Current plan:{" "}
            <span className="font-medium">
              {applied?.service_tier_label || info.service_tier_label}
            </span>
          </p>
        ) : null}

        {applied && (
          <Alert data-testid="service-tier-applied">
            <CheckCircle2 className="h-4 w-4" />
            <AlertTitle>{applied.message}</AlertTitle>
            <AlertDescription className="space-y-2 text-sm">
              {applied.after_switch_instruction && <p>{applied.after_switch_instruction}</p>}
              <p>Reload ALICE to see the features for your new plan.</p>
              <Button size="sm" variant="outline" onClick={() => window.location.reload()}>
                <RefreshCw className="h-4 w-4 mr-2" /> Reload ALICE
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {pending && (
          <div className="border border-amber-400/40 rounded-lg p-4 space-y-3" data-testid="service-tier-pending">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 shrink-0 text-amber-400 mt-0.5" />
              <div className="space-y-1 text-sm">
                <p className="font-medium text-amber-300">
                  Plan change waiting for approval: {pending.label}
                </p>
                <p className="text-white/80">
                  ALICE has moved your organisation to a new plan. It takes effect
                  once a Global Administrator or Privileged Role Administrator
                  approves the permissions below. Until then everything keeps
                  working on your current plan.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {(pending.permissions || []).map((perm) => (
                <Badge key={perm.name} variant="secondary" className="font-mono text-xs" title={perm.description}>
                  {perm.name}
                </Badge>
              ))}
            </div>
            {pending.after_switch_instruction && (
              <p className="text-xs text-white/60">After the switch: {pending.after_switch_instruction}</p>
            )}
            {!pending.available && (
              <p className="text-xs text-red-300">
                The new plan isn&apos;t available on this server yet. Contact ALICE support.
              </p>
            )}
            <div className="flex flex-wrap justify-end gap-2">
              <Button
                variant="outline"
                onClick={checkAgain}
                disabled={completing}
                data-testid="service-tier-check"
              >
                {completing ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                Check again
              </Button>
              <Button
                onClick={approve}
                disabled={completing || !pending.available || !pending.client_id}
                data-testid="service-tier-approve"
              >
                <ShieldCheck className="h-4 w-4 mr-2" />
                Approve new permissions
              </Button>
            </div>
          </div>
        )}

        {notice && (
          <Alert
            variant={notice.tone === "error" ? "destructive" : "default"}
            data-testid="service-tier-notice"
          >
            {notice.tone === "info" ? (
              <Loader2 className={`h-4 w-4 ${completing ? "animate-spin" : ""}`} />
            ) : (
              <AlertTriangle className="h-4 w-4" />
            )}
            <AlertDescription>{notice.text}</AlertDescription>
          </Alert>
        )}

        {error && (
          <Alert variant="destructive">
            <AlertTitle>Something went wrong</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}
